import useFilteredDateSlots from './useFilteredDateSlots';
import { useSlotsPerWeek } from './useSlotsPerWeek';
import useWeeklyAvailability from './useWeeklyAvailability';
import useWeeklyChosenState from './useWeeklyChosenState';
import { BookingSlot } from '../../api/search';
import { Kid } from '../../components/KidsInput';
import { formatChosenWeeks } from '../../components/booking_calendar/ChosenWeekViewer';
import { formatWeekDates } from '../../utils/weekly';

export default function useWeeklyLogic(
  slots: BookingSlot[],
  kidsWithEligibility: Kid[],
  isLoggedIn: boolean,
  initialState: Record<string, string[]>
) {
  const { slotsPerWeek, weekEntries } = useSlotsPerWeek(slots);

  const { pickTimesPerWeek, dateSlotsPerWeek } = useFilteredDateSlots(
    slotsPerWeek,
    kidsWithEligibility,
    isLoggedIn
  );

  const { availableWeekEntries, isDayAvailable } = useWeeklyAvailability(
    weekEntries,
    pickTimesPerWeek,
    dateSlotsPerWeek
  );

  const {
    chosenWeekDaytimes,
    onWeekSelect,
    onWeekRemove,
    overwriteChosenWeekDaytimes,
  } = useWeeklyChosenState(dateSlotsPerWeek, initialState);

  const selectedWeeks = formatChosenWeeks(chosenWeekDaytimes, {
    slotsPerWeek,
    dateSlotsPerWeek,
  });

  // api

  const getSessionsForWeekId = (weekCandidate: string) => ({
    dateString:
      formatWeekDates(dateSlotsPerWeek[weekCandidate]) || 'unknown date',
    slots: pickTimesPerWeek[weekCandidate],
  });

  const getCurrentChosenSlotsForWeekId = (weekCandidate: string) =>
    chosenWeekDaytimes[weekCandidate] || [];

  const getFinalSelectionData = () => {
    const allDateSlots = weekEntries
      .map((w) => w.id)
      .reduce((acc, weekId) => ({ ...acc, ...dateSlotsPerWeek[weekId] }), {});

    const weekDataConverted = {
      chosenSlots: Object.fromEntries(
        Object.keys(chosenWeekDaytimes)
          .map((weekId) =>
            Object.keys(dateSlotsPerWeek[weekId] || {}).map((dayDate) =>
              chosenWeekDaytimes[weekId]
                ? ([dayDate, chosenWeekDaytimes[weekId]] as const)
                : []
            )
          )
          .flat()
      ),
      dateSlots: allDateSlots,
    };
    return {
      ...weekDataConverted,
      slots,
      kids: kidsWithEligibility,
      bookingMode: 'weekly' as const,
      weeklyInfo: {
        chosenWeekDaytimes,
        weekEntries,
        dateSlotsPerSelectedWeek: Object.fromEntries(
          Object.entries(dateSlotsPerWeek).filter(([weekId]) =>
            Object.keys(chosenWeekDaytimes).includes(weekId)
          )
        ),
      },
    };
  };

  return {
    selection: {
      selectedWeeks,
      chosenWeekDaytimes,
      dateSlotsPerSelectedWeek: Object.fromEntries(
        Object.entries(dateSlotsPerWeek).filter(([weekId]) =>
          Object.keys(chosenWeekDaytimes).includes(weekId)
        )
      ),
    },
    availability: {
      weekEntries,
      availableWeekEntries,
    },
    api: {
      isDayAvailable,

      getSessionsForWeekId,
      getCurrentChosenSlotsForWeekId,

      onWeekSelect,
      onWeekRemove,
      overwriteChosenWeekDaytimes,

      getFinalSelectionData,
    },
  };
}
